/** Artifact phases from GET /remediation/requests/{id}/artifacts/{phase} (OpenAPI). */

export const ARTIFACT_PHASE_ORDER = [
  "preview/pre",
  "preview/playbook",
  "exec/pre",
  "exec/post",
  "exec/aws_responses",
];

export const ARTIFACT_PHASE_LABELS = {
  "preview/pre": "Preview — pre",
  "preview/playbook": "Preview — playbook",
  "exec/pre": "Execution — pre",
  "exec/post": "Execution — post",
  "exec/aws_responses": "Execution — AWS responses",
};

export const getArtifactPhaseLabel = (phase) =>
  ARTIFACT_PHASE_LABELS[phase] || phase || "Unknown phase";

/**
 * Customer-facing grouping. The API keeps five granular phases; the UI shows
 * two buckets — what was captured before the change, and what the run produced.
 */
export const ARTIFACT_PHASE_GROUPS = [
  {
    id: "pre_remediation",
    label: "Pre-Remediation",
    phases: ["preview/pre", "preview/playbook", "exec/pre"],
  },
  {
    id: "post_remediation",
    label: "Post-Remediation",
    phases: ["exec/post", "exec/aws_responses"],
  },
];

/** Sort manifest phases in canonical API order; unknown phases last. */
export const sortArtifactPhases = (phases = []) => {
  const rank = new Map(ARTIFACT_PHASE_ORDER.map((p, i) => [p, i]));
  return [...phases].sort((a, b) => {
    const ra = rank.has(a.phase) ? rank.get(a.phase) : 999;
    const rb = rank.has(b.phase) ? rank.get(b.phase) : 999;
    if (ra !== rb) return ra - rb;
    return String(a.phase).localeCompare(String(b.phase));
  });
};

/**
 * Bucket manifest entries into the two customer-facing groups, in canonical
 * phase order. Groups with no artifacts are dropped; phases the UI doesn't
 * know about are kept in a trailing "Other" group rather than hidden.
 */
export const groupArtifactPhases = (entries = []) => {
  const sorted = sortArtifactPhases(entries);
  const grouped = ARTIFACT_PHASE_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    entries: sorted.filter((entry) => group.phases.includes(entry.phase)),
  }));

  const known = new Set(ARTIFACT_PHASE_GROUPS.flatMap((group) => group.phases));
  const ungrouped = sorted.filter((entry) => !known.has(entry.phase));
  if (ungrouped.length > 0) {
    grouped.push({ id: "other", label: "Other", entries: ungrouped });
  }

  return grouped.filter((group) => group.entries.length > 0);
};

export const formatArtifactBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
